import React, { useState, useEffect } from "react";
import PageInstructions from "@/components/PageInstructions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import jsPDF from 'jspdf';

interface AnimalData {
  emoji: string;
  centenas: string[];
}

const CentenasViciadas = () => {
  const [selectedCentenas, setSelectedCentenas] = useState<Set<string>>(new Set());
  const [currentFilter, setCurrentFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [notes, setNotes] = useState("");

  const centenas_viciadas: { [key: string]: AnimalData } = {
    'AVESTRUZ': { emoji: '🦓', centenas: ['101','501','701','102','702','502','403','503','103','104','804','404'] },
    'ÁGUIA': { emoji: '🦅', centenas: ['105','905','505','206','406','806','107','807','707','108','708','808'] },
    'BURRO': { emoji: '🫏', centenas: ['109','209','409','610','510','410','011','811','511','012','712','212'] },
    'BORBOLETA': { emoji: '🦋', centenas: ['213','313','913','714','914','614','715','515','215','416','016','716'] },
    'CACHORRO': { emoji: '🐕', centenas: ['717','517','417','918','018','518','519','619','419','020','320','720'] },
    'CABRA': { emoji: '🐐', centenas: ['021','921','121','022','622','822','123','523','223','124','824','724'] },
    'CARNEIRO': { emoji: '🐑', centenas: ['525','725','025','026','626','426','027','527','127','028','828','128'] },
    'CAMELO': { emoji: '🐪', centenas: ['129','929','529','530','630','930','031','531','331','032','532','932'] },
    'COBRA': { emoji: '🐍', centenas: ['033','433','633','034','334','734','035','735','235','036','736','636'] },
    'COELHO': { emoji: '🐰', centenas: ['237','537','037','938','338','238','339','039','139','640','740','940'] },
    'CAVALO': { emoji: '🐎', centenas: ['441','541','041','242','842','442','043','343','743','144','944','044'] },
    'ELEFANTE': { emoji: '🐘', centenas: ['845','045','445','046','646','846','147','247','047','148','648','048'] },
    'GALO': { emoji: '🐓', centenas: ['549','049','949','850','550','250','551','151','351','552','252','752'] },
    'GATO': { emoji: '🐱', centenas: ['353','753','653','854','054','654','155','255','755','156','856','656'] },
    'JACARÉ': { emoji: '🐊', centenas: ['957','657','057','358','158','558','159','359','759','660','560','950'] },
    'LEÃO': { emoji: '🦁', centenas: ['161','361','061','962','262','062','763','363','663','064','764','364'] },
    'MACACO': { emoji: '🐵', centenas: ['365','165','065','466','166','866','567','267','367','168','068','868'] },
    'PORCO': { emoji: '🐷', centenas: ['069','369','569','670','370','470','171','471','871','972','872','672'] },
    'PAVÃO': { emoji: '🦚', centenas: ['373','673','973','474','174','074','775','575','175','276','476','676'] },
    'PERU': { emoji: '🦃', centenas: ['077','277','877','878','178','678','479','079','779','680','880','780'] },
    'TOURO': { emoji: '🐂', centenas: ['681','181','581','382','682','282','783','883','383','484','284','784'] },
    'TIGRE': { emoji: '🐅', centenas: ['685','085','185','686','286','486','287','787','387','188','288','688'] },
    'URSO': { emoji: '🐻', centenas: ['289','189','089','690','590','490','591','391','191','992','192','292'] },
    'VEADO': { emoji: '🦌', centenas: ['993','093','393','794','494','094','195','595','395','496','996','696'] },
    'VACA': { emoji: '🐄', centenas: ['297','397','497','998','298','698','099','599','699','500','300','700'] }
  };

  useEffect(() => {
    // Load saved notes
    const savedNotes = localStorage.getItem('centenas_viciadas_notes');
    if (savedNotes) {
      setNotes(savedNotes);
    }
  }, []);

  const toggleCentena = (centena: string) => {
    const newSelected = new Set(selectedCentenas);
    if (newSelected.has(centena)) {
      newSelected.delete(centena);
    } else {
      newSelected.add(centena);
    }
    setSelectedCentenas(newSelected);
  };

  const shouldShowAnimal = (animal: string) => {
    if (searchTerm) {
      const animalMatch = animal.toLowerCase().includes(searchTerm.toLowerCase());
      const centenaMatch = centenas_viciadas[animal].centenas.some(c => c.includes(searchTerm));
      if (!animalMatch && !centenaMatch) return false;
    }

    if (currentFilter === 'selected') {
      return centenas_viciadas[animal].centenas.some(c => selectedCentenas.has(c));
    }

    return true;
  };

  const filteredAnimals = Object.entries(centenas_viciadas).filter(([animal]) => shouldShowAnimal(animal));

  const saveNotes = () => {
    localStorage.setItem('centenas_viciadas_notes', notes);
    const data = {
      notes: notes,
      selectedCentenas: Array.from(selectedCentenas),
      timestamp: new Date().toISOString()
    };
    alert('✅ Anotações salvas com sucesso!');
  };

  const clearAll = () => {
    if (confirm('⚠️ Tem certeza que deseja limpar todas as seleções e anotações?')) {
      setSelectedCentenas(new Set());
      setNotes('');
      setSearchTerm('');
      setCurrentFilter('all');
      localStorage.removeItem('centenas_viciadas_notes');
    }
  };

  const exportData = () => {
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleString('pt-BR');
    
    // Header
    doc.setFontSize(20);
    doc.text('Centenas Viciadas - Relatório', 20, 20);
    
    doc.setFontSize(12);
    doc.text(`Data de Exportação: ${currentDate}`, 20, 35);
    doc.text(`Total de Centenas Selecionadas: ${selectedCentenas.size}`, 20, 45);
    
    let yPos = 65;
    
    // Selected centenas
    if (selectedCentenas.size > 0) {
      doc.setFontSize(14);
      doc.text('Centenas Selecionadas:', 20, yPos);
      yPos += 15;
      
      const selectedArray = Array.from(selectedCentenas).sort();
      let xPos = 20;
      
      selectedArray.forEach((centena, index) => {
        doc.setFontSize(12);
        doc.text(centena, xPos, yPos);
        xPos += 25;
        
        if ((index + 1) % 7 === 0) {
          yPos += 15;
          xPos = 20;
        }
      });
      
      yPos += 30;
    }
    
    // Notes
    if (notes) {
      doc.setFontSize(14);
      doc.text('Anotações:', 20, yPos);
      
      const splitNotes = doc.splitTextToSize(notes, 170);
      doc.setFontSize(10);
      doc.text(splitNotes, 20, yPos + 15);
    }
    
    doc.save(`centenas_viciadas_${new Date().toISOString().split('T')[0]}.pdf`);
    alert('📤 PDF exportado com sucesso!');
  };

  const totalCentenas = Object.values(centenas_viciadas).reduce((total, animal) => total + animal.centenas.length, 0);

  return (
    <div className="w-full space-y-6">
      <PageInstructions
        title="📖 Como usar as Centenas Viciadas"
        steps={[
          "Veja a lista de todos os 25 bichos com suas centenas mais frequentes",
          "Use o campo de busca para encontrar um bicho ou centena específica",
          "Observe os bichos em maior atraso — são os mais propensos a sair",
          "Clique em um bicho para ver suas centenas 'viciadas' (mais sorteadas)",
          "Aposte nas centenas destacadas em vermelho — são as mais quentes",
          "Use o botão de exportar PDF para salvar sua análise"
        ]}
      />
      {/* Header */}
      <Card className="border-0">
        <CardContent className="p-0">
          <div className="bg-gradient-to-r from-red-500 to-red-700 text-white p-8 rounded-lg">
            <h1 className="text-4xl font-bold text-center mb-4">🎯 Centenas Viciadas</h1>
            <p className="text-xl text-center mb-4 opacity-90">
              Sistema completo para análise das centenas mais frequentes no Jogo do Bicho
            </p>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center">
              <strong>💡 Dica:</strong> Observe os bichos em maior atraso e aposte nas centenas viciadas correspondentes!
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center mb-4">
            <input
              type="text"
              placeholder="🔍 Pesquisar por animal ou centena..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 p-4 border-2 border-gray-300 rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
            />
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => setCurrentFilter('all')}
                className={currentFilter === 'all' ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'}
              >
                Todos
              </Button>
              <Button
                onClick={() => setCurrentFilter('selected')}
                className={currentFilter === 'selected' ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'}
              >
                Selecionados
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Animals Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAnimals.map(([animal, data]) => {
          const selectedCount = data.centenas.filter(c => selectedCentenas.has(c)).length;
          const isHighlighted = selectedCount > 0;

          return (
            <Card 
              key={animal} 
              className={`transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                isHighlighted ? 'border-2 border-red-400 bg-gradient-to-br from-red-50 to-pink-50' : ''
              }`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{data.emoji}</span>
                    <span className="font-bold text-slate-700">{animal}</span>
                  </div>
                  <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                    {data.centenas.length}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-2">
                  {data.centenas.map((centena) => (
                    <button
                      key={centena}
                      onClick={() => toggleCentena(centena)}
                      className={`p-2 rounded-lg text-center font-bold text-sm transition-all duration-300 hover:scale-110 ${
                        selectedCentenas.has(centena)
                          ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                          : 'bg-gradient-to-r from-gray-200 to-gray-300 text-slate-700 hover:from-blue-500 hover:to-blue-600 hover:text-white'
                      }`}
                    >
                      {centena}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Selected Numbers */}
      {selectedCentenas.size > 0 && (
        <Card className="border-2 border-orange-300 bg-gradient-to-br from-yellow-50 to-orange-50 animate-in slide-in-from-bottom-4 duration-500">
          <CardHeader>
            <CardTitle className="text-orange-600 flex items-center gap-3">
              🎲 Centenas Selecionadas: {selectedCentenas.size}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Array.from(selectedCentenas).sort().map((centena) => (
                <div
                  key={centena}
                  className="bg-gradient-to-r from-orange-400 to-orange-500 text-white px-4 py-2 rounded-full font-bold flex items-center gap-2"
                >
                  {centena}
                  <button
                    onClick={() => toggleCentena(centena)}
                    className="bg-white/30 hover:bg-white/50 rounded-full w-5 h-5 flex items-center justify-center text-xs transition-colors"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-6 text-center">
            <div className="text-3xl font-bold mb-2">{Object.keys(centenas_viciadas).length}</div>
            <div className="text-sm opacity-90">Animais Disponíveis</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-6 text-center">
            <div className="text-3xl font-bold mb-2">{totalCentenas}</div>
            <div className="text-sm opacity-90">Centenas Totais</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-6 text-center">
            <div className="text-3xl font-bold mb-2">{selectedCentenas.size}</div>
            <div className="text-sm opacity-90">Selecionadas</div>
          </CardContent>
        </Card>
      </div>

      {/* Notes Section */}
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="text-slate-700">📝 Campo de Anotações:</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <textarea
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              localStorage.setItem('centenas_viciadas_notes', e.target.value);
            }}
            placeholder="Faça suas anotações sobre estratégias, bichos em atraso, resultados observados..."
            className="w-full min-h-32 p-4 border-2 border-gray-300 rounded-xl resize-y focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          
          <div className="flex flex-wrap gap-4">
            <Button onClick={saveNotes} className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700">
              💾 Salvar Anotações
            </Button>
            <Button onClick={clearAll} variant="outline" className="border-gray-400 hover:bg-gray-100">
              🗑️ Limpar Tudo
            </Button>
            <Button onClick={exportData} variant="outline" className="border-gray-400 hover:bg-gray-100">
              📤 Exportar PDF
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CentenasViciadas;